import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { gameStore, loadSession } from '../lib/game-store';
import { audioManager } from '../lib/audio-manager';
import { MECHANIC_INFO } from '@juegazo/content';
import type { GameStateSnapshot, PlayerId, PlayerSnapshot, VoteChoice, ReturnTarget } from '@juegazo/shared';
import './background';
import './voice-controls';
import './vote-modal';
import type { VoiceManager } from '../lib/voice-manager';

@customElement('jgz-play')
export class JgzPlay extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String }) roomId = '';
  @property({ type: Object }) state: GameStateSnapshot | null = null;
  @property({ type: Object }) lastExplosion: { playerId: PlayerId; displayName: string } | null = null;
  @property({ attribute: false }) voiceManager: VoiceManager | null = null;
  @property({ attribute: false }) lastError: { code: string; message: string } | null = null;
  @state() private showEndConfirm = false;
  @state() private showChangeConfirm = false;
  @state() private flashMessage: string | null = null;
  @state() private soundOn = audioManager.isEnabled();
  /** Último error del servidor (room:error) visible para el jugador. */
  @state() private visibleError: string | null = null;
  private errorTimer: number | null = null;

  private get session() {
    return loadSession();
  }

  private get isAdmin(): boolean {
    return this.state?.players.find((p) => p.id === this.session.playerId)?.isAdmin ?? false;
  }

  private get isActivePlayer(): boolean {
    return this.state?.currentPlayerId === this.session.playerId;
  }

  private get me() {
    return this.state?.players.find((p) => p.id === this.session.playerId);
  }

  private get activePlayer() {
    return this.state?.players.find((p) => p.id === this.state?.currentPlayerId);
  }

  /**
   * Jugadores que participan del desempate: los empatados mientras el
   * desempate está listo y los que siguen en pie durante la ronda.
   */
  private get tiebreakParticipants(): PlayerSnapshot[] {
    const ids = this.state?.tiebreakPlayers ?? [];
    return ids
      .map((id) => this.state?.players.find((p) => p.id === id))
      .filter((p): p is PlayerSnapshot => Boolean(p));
  }

  /**
   * Sólo el administrador o un jugador empatado puede iniciar el desempate
   * (GAME-TIE-002).
   */
  private get canStartTiebreak(): boolean {
    if (this.isAdmin) return true;
    const playerId = this.session.playerId;
    if (!playerId) return false;
    return (this.state?.tiebreakPlayers ?? []).includes(playerId);
  }

  /** true mientras la ronda en pantalla es una ronda de desempate. */
  private get isTiebreakRound(): boolean {
    const st = this.state?.state;
    if (st === 'tiebreak_playing') return true;
    if (st === 'paused') return (this.state?.tiebreakPlayers?.length ?? 0) > 0;
    return false;
  }

  private passBomb = () => {
    gameStore.send('bomb:pass');
  };

  private returnBomb = () => {
    gameStore.send('bomb:return');
  };

  private pauseGame = () => {
    gameStore.send('game:pause');
  };

  private resumeGame = () => {
    gameStore.send('game:resume');
  };

  private requestChangePrompt = () => {
    this.showChangeConfirm = true;
  };

  private confirmChangePrompt = () => {
    gameStore.send('content:change');
    this.showChangeConfirm = false;
    this.flashMessage = 'La consigna cambió. El tiempo restante no se modificó.';
    setTimeout(() => (this.flashMessage = null), 3500);
  };

  private cancelChangePrompt = () => {
    this.showChangeConfirm = false;
  };

  private endGame = () => {
    this.showEndConfirm = true;
  };

  private confirmEnd = () => {
    gameStore.send('game:finish');
    this.showEndConfirm = false;
  };

  /**
   * Avanza a la siguiente bomba. El empate se detecta DESPUÉS de la
   * explosión (en `advanceBetweenBombs`), así que este camino nunca puede
   * iniciar el desempate: cuando hay empate el servidor deja la sala en
   * `tiebreak_ready` y el cliente muestra la pantalla de desempate, donde el
   * botón "Iniciar desempate" manda `tiebreak:start`.
   */
  private startNextBomb = () => {
    gameStore.send('bomb:next');
  };

  private startTiebreak = () => {
    gameStore.send('tiebreak:start');
  };

  private cancelEnd = () => {
    this.showEndConfirm = false;
  };

  private leave = () => {
    this.dispatchEvent(new CustomEvent('jgz-leave', { bubbles: true, composed: true }));
  };

  private callVote = () => {
    gameStore.send('vote:start');
  };

  private onVoteCast = (e: CustomEvent<{ choice: VoteChoice }>) => {
    gameStore.send('vote:cast', e.detail.choice);
  };

  private onVoteDecide = (e: CustomEvent<{ target: ReturnTarget }>) => {
    gameStore.send('vote:decide', e.detail.target);
  };

  private onVoteClear = () => {
    gameStore.send('vote:clear');
  };

  private toggleSound = () => {
    this.soundOn = !this.soundOn;
    audioManager.setEnabled(this.soundOn);
  };

  connectedCallback(): void {
    super.connectedCallback();
    audioManager.unlock();
    this.soundOn = audioManager.isEnabled();
    audioManager.setEnabled(this.soundOn);
    this.addEventListener('jgz-vote-cast', this.onVoteCast as EventListener);
    this.addEventListener('jgz-vote-decide', this.onVoteDecide as EventListener);
    this.addEventListener('jgz-vote-clear', this.onVoteClear as EventListener);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    audioManager.teardown();
    if (this.errorTimer !== null) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
    this.removeEventListener('jgz-vote-cast', this.onVoteCast as EventListener);
    this.removeEventListener('jgz-vote-decide', this.onVoteDecide as EventListener);
    this.removeEventListener('jgz-vote-clear', this.onVoteClear as EventListener);
  }

  protected willUpdate(changed: Map<string, unknown>) {
    if (changed.has('lastError')) {
      const error = this.lastError;
      // Los errores de reconexión se muestran en la pantalla de reconexión.
      if (error && error.code !== 'reconnect_failed' && error.code !== 'reconnect_timeout') {
        this.showError(error.message);
      }
    }
  }

  updated(changed: Map<string, unknown>) {
    if (!this.state) return;
    audioManager.sync({
      state: this.state.state,
      paused: this.state.paused,
      tensionState: this.state.tensionState,
      bombNumber: this.state.bombNumber,
      remainingTimeMs: this.state.remainingTimeMs,
      currentPlayerId: this.state.currentPlayerId ?? null,
      returnWindowMsRemaining: this.state.returnWindowMsRemaining ?? 0,
    });
    void changed;
  }

  render() {
    if (!this.state) {
      const errorMessage =
        this.lastError?.code === 'reconnect_failed' || this.lastError?.code === 'reconnect_timeout'
          ? this.lastError.message
          : null;
      return html`
        <div class="container fade-in" style="max-width: 480px; margin: 0 auto;">
          <div class="card" style="text-align: center;">
            <div style="font-size: 3.5rem;">${errorMessage ? '⚠️' : '🔌'}</div>
            <h2>${errorMessage ?? 'Reconectando con la sala…'}</h2>
            <p class="muted">${errorMessage ? 'Volvé al inicio y unite a una sala activa.' : 'Si esto tarda demasiado, intentá recargar la página.'}</p>
            <div style="display: flex; justify-content: center; margin-top: 1rem;">
              <button class="btn-pill primary" @click=${this.leave} style="width: auto; min-width: 200px;">
                <span class="label">VOLVER AL INICIO</span>
              </button>
            </div>
          </div>
        </div>
      `;
    }
    if (this.state.state === 'finished') {
      return html`
        <div class="container fade-in" style="max-width: 480px; margin: 0 auto; text-align: center; padding-top: 4rem;">
          <div class="stack">
            <div style="font-size: 4rem;">🏆</div>
            <h1>¡Partida finalizada!</h1>
            <p class="muted">Mostrando los resultados…</p>
          </div>
        </div>
      `;
    }
    if (this.state.state === 'tiebreak_ready') {
      return this.renderTiebreakReady();
    }
    if (this.state.state === 'exploded' && this.state.lastExplosionPlayerId) {
      return this.renderExplosion();
    }
    return this.renderPlaying();
  }

  private renderPlaying() {
    const showPauseAndChange = this.isAdmin && this.state!.paused;
    const canReturn =
      this.isActivePlayer &&
      !this.state!.paused &&
      (this.state!.returnWindowMsRemaining ?? 0) > 0;

    return html`
      <jgz-background></jgz-background>
      <div class="game-layout fade-in" style="position: relative; z-index: 1;">
        <div class="stack">
          ${this.renderHeader()}
          ${this.isTiebreakRound ? this.renderTiebreakBanner() : nothing}
          ${this.renderConsignaCard()}
          ${this.renderTurnCard()}
          ${this.renderBombCircle()}
          ${this.renderActionButtons(showPauseAndChange, canReturn)}
        </div>
      </div>
      ${this.showChangeConfirm ? this.renderChangeConfirm() : nothing}
      ${this.showEndConfirm ? this.renderEndConfirm() : nothing}
      ${this.renderErrorBanner()}
      ${this.state!.voting
        ? html`<jgz-vote-modal
            .voting=${this.state!.voting}
            .myPlayerId=${this.session.playerId}
            .isAdmin=${this.isAdmin}
            .isLocalMode=${false}
            .currentPlayerId=${this.state!.currentPlayerId ?? ''}
            .players=${this.state!.players}
          ></jgz-vote-modal>`
        : nothing}
      ${this.flashMessage ? this.renderFlash() : nothing}
    `;
  }

  private renderHeader() {
    return html`
      <div class="row header-row" style="justify-content: space-between; flex-wrap: wrap; gap: 0.4rem; flex-shrink: 0;">
        <div class="juegazo-logo-wrap">
          <div class="juegazo-logo" style="font-family: 'Bungee', 'Impact', var(--font); letter-spacing: 0.04em; background: linear-gradient(135deg, var(--yellow) 0%, var(--orange) 100%); -webkit-background-clip: text; background-clip: text; color: transparent; line-height: 1; font-weight: 900;">¡JUEGAZO!</div>
        </div>
        <div class="row" style="gap: 0.4rem; align-items: center;">
          <button class="icon-btn-circle sound-toggle ${this.soundOn ? '' : 'muted'}" @click=${this.toggleSound} title=${this.soundOn ? 'Silenciar' : 'Activar sonido'}>
            ${this.soundOn
              ? html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H2v6h4l5 4V5zM15 8a4 4 0 0 1 0 8M19 5a8 8 0 0 1 0 14"/></svg>`
              : html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6"/></svg>`}
          </button>
          ${this.voiceManager
            ? html`<jgz-voice-controls .voiceManager=${this.voiceManager}></jgz-voice-controls>`
            : nothing}
          <button class="icon-btn-circle primary" @click=${this.leave}>
            <span>Salir</span>
          </button>
        </div>
      </div>
      <div class="row" style="gap: 0.4rem; align-items: center; flex-shrink: 0; justify-content: center;">
        <div class="chip" style="background: rgba(108, 92, 231, 0.28); border-color: rgba(108, 92, 231, 0.5);">
          <div class="chip-icon-circle" style="color: var(--purple-2);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 21h10M9 21v-4M15 21v-4M6 4h12v6a6 6 0 1 1-12 0V4z"/><path d="M6 4h12M4 4h16"/></svg>
          </div>
          <span>Ronda <strong>${this.state!.currentRound}</strong></span>
        </div>
        <div class="chip" style="background: rgba(0, 184, 148, 0.28); border-color: rgba(0, 184, 148, 0.5);">
          <div class="chip-icon-circle" style="color: var(--teal-2);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M9 9h.01M15 9h.01M9 15c.85.6 1.85 1 3 1s2.15-.4 3-1"/></svg>
          </div>
          <span>Juego <strong>${this.state!.bombNumber}</strong> / ${this.state!.totalBombs}</span>
        </div>
      </div>
    `;
  }

  private renderConsignaCard() {
    const mechanic = this.state?.selectedMechanic;
    return html`
      <div class="card" style="background: linear-gradient(135deg, rgba(0, 184, 148, 0.32), rgba(0, 184, 148, 0.18)); border-color: rgba(0, 184, 148, 0.5); position: relative; overflow: hidden; flex-shrink: 0;">
        <div class="consigna-content" style="text-align: center;">
          ${mechanic
            ? html`<div class="mechanic-subtitle">${MECHANIC_INFO[mechanic].name}</div>`
            : nothing}
          ${this.state!.promptLines.map(
            (line) => html`<p class="consigna-prompt">${line}</p>`,
          )}
        </div>
      </div>
    `;
  }

  private renderTurnCard() {
    return html`
      <div class="card" style="background: linear-gradient(135deg, rgba(108, 92, 231, 0.32), rgba(108, 92, 231, 0.18)); border-color: rgba(108, 92, 231, 0.5); flex-shrink: 0;">
        <div class="row" style="gap: 0.85rem; align-items: center;">
          <div class="card-icon" style="background: rgba(255,255,255,0.12); display:inline-flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--purple-2);">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a8 8 0 0 1 16 0v1"/></svg>
          </div>
          <div>
            <div class="section-label">TURNO DE</div>
            <div class="player-name">${this.activePlayer?.displayName ?? '...'}</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderBombCircle() {
    const center = this.isActivePlayer ? '💣' : '✋';
    const centerLabel = this.isActivePlayer ? 'TENGO LA BOMBA' : 'NO TENGO LA BOMBA';
    const inminent = this.state!.tensionState === 'inminente';
    const acelerado = this.state!.tensionState === 'acelerado';
    const heartbeatClass = inminent ? 'heartbeat-fast' : acelerado ? '' : 'heartbeat-slow';
    const ringClassExtra = inminent ? 'pulse-ring-fast' : '';
    const ringColor = inminent ? 'var(--red)' : acelerado ? 'var(--yellow)' : 'var(--teal)';
    const paused = this.state!.paused;
    const pausedByName = this.state!.players.find((p) => p.id === this.state!.pausedBy)?.displayName;
    return html`
      <div class="bomb-container">
        <div class="dots-left"></div>
        <div class="dots-right"></div>
        <div class="bomb-stage" style="position: relative; display: flex; align-items: center; justify-content: center;">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 240 240"
            preserveAspectRatio="xMidYMid meet"
            style="position: absolute; top: 0; left: 0; overflow: visible;"
          >
            <defs>
              <linearGradient id="bomb-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="var(--teal)"/>
                <stop offset="50%" stop-color="var(--yellow)"/>
                <stop offset="100%" stop-color="var(--red)"/>
              </linearGradient>
            </defs>
            <circle
              class="pulse-ring-base ${ringClassExtra}"
              cx="120" cy="120" r="105"
              stroke="url(#bomb-grad)"
              stroke-width="12"
              style="--ring-color: ${ringColor};"
            />
            <circle
              class="pulse-ring-pulse ${ringClassExtra}"
              cx="120" cy="120" r="105"
              stroke-dasharray="90 555"
              stroke-width="14"
              style="--ring-color: ${ringColor};"
            />
          </svg>
          ${paused
            ? html`<div
                style="position: absolute; z-index: 3; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.4rem; background: rgba(5, 8, 17, 0.6); backdrop-filter: blur(4px); border-radius: 50%; margin: 2rem;"
              >
                <div style="font-size: 3rem;">⏸</div>
                <div style="font-weight: 900; letter-spacing: 0.05em; font-size: 1rem; color: var(--yellow);">JUEGO PAUSADO</div>
                ${pausedByName
                  ? html`<div style="font-size: 1rem; color: var(--muted);">por ${pausedByName}</div>`
                  : nothing}
              </div>`
            : html`<div
                class="heartbeat ${heartbeatClass} ${inminent ? 'bomb-shake' : ''} bomb-emoji"
                style="user-select: none; position: relative; z-index: 2;"
              >
                ${center}
              </div>`}
        </div>
        ${this.isActivePlayer
          ? html`<p class="center-label" style="color: var(--orange-2); margin: 0;">${centerLabel}</p>`
          : html`<p class="center-label muted-text" style="margin: 0;">${centerLabel}</p>`}
      </div>
    `;
  }

  private renderActionButtons(showPauseAndChange: boolean, canReturn: boolean) {
    const returnSeconds = Math.ceil((this.state?.returnWindowMsRemaining ?? 0) / 1000);
    const passDisabled = !this.isActivePlayer || this.state!.paused;
    const paused = this.state!.paused;
    const canResume = paused && (this.state!.pausedBy === this.session.playerId || this.isAdmin);
    const pauseLabel = paused ? 'REANUDAR' : 'PAUSA';
    return html`
      <div class="row" style="gap: 0.6rem; flex-wrap: wrap; flex-shrink: 0; align-items: stretch;">
        <button class="btn-pill green" ?disabled=${passDisabled} @click=${this.passBomb}>
          <div class="ico">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M5 4l14 8-14 8V4z"/></svg>
          </div>
          <span class="label">PASAR</span>
          <span class="sublabel">TURNO A OTRO JUGADOR</span>
        </button>
        <button
          class="btn-pill gray"
          ?disabled=${paused && !canResume}
          @click=${paused ? this.resumeGame : this.pauseGame}
          title=${paused && !canResume ? 'Solo el pauser o el admin puede reanudar' : ''}
        >
          <div class="ico">
            ${paused
              ? html`<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M5 4l14 8-14 8V4z"/></svg>`
              : html`<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>`}
          </div>
          <span class="label">${pauseLabel}</span>
          <span class="sublabel">juego</span>
        </button>
      </div>
      <div class="row" style="flex-shrink: 0;">
        <button class="btn-pill primary" style="flex: 1;" ?disabled=${!canReturn} @click=${this.returnBomb}>
          <div class="ico">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 11l9-7 9 7v10a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-5h-2v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V11z" transform="scale(-1 1) translate(-24 0)"/></svg>
          </div>
          <span class="label">DEVOLVER</span>
          <span class="sublabel">${returnSeconds > 0 ? `${returnSeconds}s para devolver` : 'turno'}</span>
        </button>
      </div>
      ${showPauseAndChange
        ? html`
            <button class="btn-pill gray btn-action-menu" @click=${this.requestChangePrompt} style="font-size: 1rem;">
              <span class="label" style="font-size: 1rem;">CAMBIAR CONSIGNA</span>
            </button>
          `
        : nothing}
      ${paused && !this.state!.voting
        ? html`
            <button class="btn-pill gray btn-action-menu" @click=${this.callVote} style="font-size: 1rem;">
              <span class="label" style="font-size: 1rem;">LLAMAR A VOTACIÓN</span>
            </button>
          `
        : nothing}
      ${this.isAdmin && !paused
        ? html`
            <button class="btn-pill gray btn-compact" @click=${this.endGame} style="font-size: 1rem;">
              <span class="label" style="font-size: 1rem;">FINALIZAR PARTIDA</span>
            </button>
          `
        : nothing}
    `;
  }

  /**
   * Pantalla de desempate lista (S13). Muestra quiénes están empatados y
   * ofrece el botón "Iniciar desempate" al administrador o a un empatado.
   */
  private renderTiebreakReady() {
    const participants = this.tiebreakParticipants;
    return html`
      <jgz-background></jgz-background>
      <div class="container fade-in" style="max-width: 560px; margin: 0 auto; padding-top: 3rem; position: relative; z-index: 1;">
        <div class="stack" style="align-items: center;">
          <div style="font-size: 3rem;">⚔️</div>
          <h1 style="font-size: 2rem; margin: 0;">DESEMPATE</h1>
          <p class="muted" style="text-align: center; margin: 0;">
            Hubo empate. Se juega una bomba de desempate entre los jugadores empatados:
            a quien le explote queda eliminado y el último en pie gana la partida.
          </p>
          <div class="card" style="width: 100%;">
            <div class="section-label">EMPATADOS</div>
            <ul
              data-testid="tiebreak-players"
              style="list-style: none; padding: 0; margin: 0.5rem 0 0; display: flex; flex-direction: column; gap: 0.4rem;"
            >
              ${participants.map((p) => html`<li style="font-weight: 700;">${p.displayName}</li>`)}
            </ul>
          </div>
          ${this.canStartTiebreak
            ? html`
                <button class="btn-pill primary" data-testid="tiebreak-start" @click=${this.startTiebreak} style="width: auto;">
                  <span class="label">Iniciar desempate</span>
                  <span class="sublabel">Tiebreak</span>
                </button>
              `
            : html`
                <p class="muted" style="text-align: center; margin: 0;">
                  Esperando a que un jugador empatado o el administrador inicie el desempate…
                </p>
              `}
        </div>
      </div>
      ${this.renderErrorBanner()}
    `;
  }

  /** Banner de contexto del desempate durante la ronda (`tiebreak_playing`). */
  private renderTiebreakBanner() {
    const participants = this.tiebreakParticipants;
    return html`
      <div
        class="card"
        data-testid="tiebreak-banner"
        style="background: linear-gradient(135deg, rgba(255, 149, 0, 0.30), rgba(255, 149, 0, 0.16)); border-color: rgba(255, 149, 0, 0.55); flex-shrink: 0; text-align: center;"
      >
        <div class="section-label" style="color: var(--orange-2);">DESEMPATE</div>
        <p style="margin: 0.35rem 0 0; font-weight: 700;">
          ${participants.map((p) => p.displayName).join(' · ')}
        </p>
        <p class="muted" style="margin: 0.25rem 0 0; font-size: 0.9rem;">
          Sólo participan los empatados. A quien le explote la bomba queda eliminado.
        </p>
      </div>
    `;
  }

  /** Muestra el último error del servidor (room:error) en la vista de juego. */
  private renderErrorBanner() {
    if (!this.visibleError) return nothing;
    return html`
      <div
        data-testid="game-error"
        style="position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: rgba(255, 77, 109, 0.95); color: #fff; padding: 0.85rem 1.5rem; border-radius: 14px; box-shadow: var(--shadow); font-weight: 600; max-width: 90vw; z-index: 120; animation: fade-in 0.3s ease;"
      >
        ${this.visibleError}
      </div>
    `;
  }

  private showError(message: string) {
    this.visibleError = message;
    if (this.errorTimer !== null) clearTimeout(this.errorTimer);
    this.errorTimer = window.setTimeout(() => {
      this.visibleError = null;
      this.errorTimer = null;
    }, 6000);
  }

  private renderFlash() {
    return html`
      <div
        style="position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: var(--yellow); color: #1F1F38; padding: 0.85rem 1.5rem; border-radius: 14px; box-shadow: var(--shadow); font-weight: 600; max-width: 90vw; z-index: 100; animation: fade-in 0.3s ease;"
      >
        ${this.flashMessage}
      </div>
    `;
  }

  private renderChangeConfirm() {
    return html`
      <div
        style="position: fixed; inset: 0; background: rgba(5, 8, 17, 0.85); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 1.5rem;"
      >
        <div class="card fade-in" style="max-width: 480px; width: 100%; background: var(--bg-2);">
          <h2>Cambiar consigna</h2>
          <p style="font-weight: 600; color: var(--orange-2); margin: 1rem 0;">
            Cambiar la consigna no modifica el tiempo restante de la bomba.
          </p>
          <div class="row" style="justify-content: flex-end;">
            <button @click=${this.cancelChangePrompt}>Cancelar</button>
            <button class="primary" @click=${this.confirmChangePrompt}>Cambiar</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderEndConfirm() {
    return html`
      <div
        style="position: fixed; inset: 0; background: rgba(5, 8, 17, 0.85); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 1.5rem;"
      >
        <div class="card fade-in" style="max-width: 480px; width: 100%; background: var(--bg-2); border-color: var(--orange);">
          <h2>¿TERMINAR PARTIDA?</h2>
          <p style="margin: 1rem 0;">La bomba actual no contará para los resultados.</p>
          <p style="margin: 0.5rem 0; color: var(--muted);">Se conservarán los resultados de las bombas completadas.</p>
          <div class="row" style="justify-content: flex-end; margin-top: 1.5rem;">
            <button @click=${this.cancelEnd}>Volver al juego</button>
            <button class="primary" @click=${this.confirmEnd}>Terminar partida</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderExplosion() {
    const exploder = this.state?.players.find((p) => p.id === this.state?.lastExplosionPlayerId);
    const meLost = this.state?.lastExplosionPlayerId === this.session.playerId;
    const bombNumber = this.state?.bombNumber ?? 0;
    const totalBombs = this.state?.totalBombs ?? 0;
    // El empate se detecta DESPUÉS de la explosión (en `advanceBetweenBombs`),
    // así que el cliente no puede saberlo por su cuenta: el motor lo publica.
    // `pendingTiebreak` dice si la próxima pantalla es el desempate en lugar
    // de los resultados; `tiebreakPhase` dice si la ronda que acaba de
    // explotar era una ronda de desempate.
    const pendingTiebreak = this.state?.pendingTiebreak ?? false;
    const isTiebreakExplosion = this.state?.tiebreakPhase ?? false;
    const isLastBomb = bombNumber >= totalBombs;
    // Cuando hay empate pendiente, el botón NO puede prometer los resultados:
    // lo que viene es el desempate.
    const adminHint = pendingTiebreak
      ? 'Esta ronda terminó en empate: lo que viene es el desempate de los que quedan.'
      : isTiebreakExplosion
        ? 'Sigue el desempate: la bomba ahora pasa entre los que quedan.'
        : isLastBomb
          ? 'Esta fue la última bomba. Muestra los resultados cuando todos estén listos.'
          : 'Inicia la siguiente bomba cuando todos estén listos.';
    const playerHint = pendingTiebreak
      ? 'Empate detectado: esperando al admin para iniciar el desempate…'
      : isTiebreakExplosion
        ? 'Esperando al admin para seguir con el desempate…'
        : isLastBomb
          ? 'Última bomba. Esperando al admin para mostrar los resultados…'
          : 'Esperando que el admin inicie la siguiente bomba…';
    const buttonLabel = pendingTiebreak
      ? '🤝 PREPARAR DESEMPATE'
      : isTiebreakExplosion
        ? '▶ SIGUIENTE BOMBA DE DESEMPATE'
        : isLastBomb
          ? '🏁 MOSTRAR RESULTADOS'
          : '▶ SIGUIENTE BOMBA';
    const buttonSublabel = pendingTiebreak
      ? 'Empate: los que quedan jugarán el desempate'
      : isTiebreakExplosion
        ? 'Desempate'
        : isLastBomb
          ? 'Final de la partida'
          : `Bomba ${bombNumber + 1} / ${totalBombs}`;
    return html`
      <div class="container fade-in" style="text-align: center; padding-top: 4rem;">
        <div class="stack" style="align-items: center;">
          <div style="font-size: 7rem; line-height: 1;">${meLost ? '💀' : '😇'}</div>
          <h1 style="font-size: 2rem; margin: 0;">
            ${meLost ? '¡Te explotó la bomba!' : '¡Sobreviviste!'}
          </h1>
          <p style="font-size: 1.1rem; color: var(--muted); margin: 0;">
            A <strong style="color: var(--fg);">${exploder?.displayName ?? 'alguien'}</strong> le explotó la bomba.
          </p>
          <div class="card" style="background: rgba(255, 255, 255, 0.04); margin-top: 1.5rem; display: flex; flex-direction: column; align-items: center; gap: 0.6rem; padding: 0.6rem;">
            ${this.isAdmin
              ? html`
                  <p class="muted" style="margin: 0; text-align: center;">${adminHint}</p>
                  <button class="btn-pill primary" style="align-self: center;" @click=${this.startNextBomb}>
                    <span class="label">${buttonLabel}</span>
                    <span class="sublabel">${buttonSublabel}</span>
                  </button>
                `
              : html`<p class="muted" style="margin: 0; text-align: center;">${playerHint}</p>`}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-play': JgzPlay;
  }
}
